import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { AppImage } from '@/components/atoms';
import { MediaPlate } from '@/components/molecules';
import { type AccessoryCategory, type AccessoryImage } from '@/services/accessories/types';

import { categoryIcon, categoryPattern } from './categoryMeta';

type Props = {
  image: AccessoryImage | null;
  /** Used when the image carries no alt text of its own. */
  fallbackAlt: string;
  category: AccessoryCategory;
  priority?: boolean;
  sizes?: string;
  /** 4:3 on a card, 1:1 on the detail page, where the part is the subject. */
  ratio?: string;
  /**
   * Says, under the plate, that the photograph has not arrived.
   *
   * The detail page only. On a card the plate is small, one of twelve, and
   * plainly a motif; at 560px square it is the subject of the screen, and an
   * empty square that large reads as an image that failed to load rather than
   * one that was never supplied. The vehicle page answers the same problem the
   * same way (§4).
   */
  caption?: boolean;
};

/**
 * An accessory photograph in a fixed frame, or a composed plate when there is
 * none.
 *
 * Every accessory has `cover: null` today, so the plate is the normal case
 * rather than the exception — which is exactly why it is not a grey box with
 * a camera glyph. It is the band's own texture with the shelf's icon on it,
 * in the site's language, and it is meant to look deliberate for as long as it
 * is standing in for a photograph (see MediaPlate).
 *
 * The plate is `aria-hidden` through `MediaPlate` and the frame carries no
 * `role="img"`: the card's title and its fitment line already say what the
 * part is, and a screen reader that also had to hear "alloy wheel, photo
 * coming soon" would be told the same thing twice.
 */
export function AccessoryPhoto({
  image,
  fallbackAlt,
  category,
  priority = false,
  sizes,
  ratio = '4 / 3',
  caption = false,
}: Props) {
  return (
    <Box
      sx={{
        position: 'relative',
        aspectRatio: ratio,
        bgcolor: 'surfaceVariant',
        overflow: 'hidden',
      }}
    >
      {image ? (
        <AppImage
          src={image.url}
          alt={image.alt ?? fallbackAlt}
          width={image.width ?? 1200}
          height={image.height ?? 900}
          priority={priority}
          {...(sizes ? { sizes } : {})}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : (
        <>
          <MediaPlate pattern={categoryPattern(category)} icon={categoryIcon(category)} />
          {caption && (
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{
                position: 'absolute',
                bottom: 12,
                left: 0,
                right: 0,
                textAlign: 'center',
              }}
            >
              Photograph coming soon
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}
